import { getAuthInstance } from '@taki/auth';
import type { TakiConfig } from '@taki/runtime-config';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { AskTakiError, askTakiErrorMessage, needsFreshToken, refusedBeforeWork } from './askTakiError.js';

export interface AskLine {
  role: 'user' | 'assistant';
  text: string;
}

export interface AskReply {
  text: string;
  toolsUsed: string[];
  steps?: ToolStep[];
  records?: RecordLink[];
  status?: 'complete' | 'paused';
  resumeId?: string;
}

export interface RecordLink { id: string; title: string; appId: string; doorId: string; doorName?: string; family?: string; hint?: string }
/** Une app ou une porte que Lia propose d'ouvrir (« ouvre Croquis ») : une carte, sans fiche. */
export interface PlaceLink { appId: string; appName: string; doorId?: string; doorLabel?: string; nature: 'fiches' | 'canvas' | 'outil' }

export type ToolStep = Extract<AskEvent, { type: 'tool' }>;

export type AskEvent =
  | { type: 'status'; phase: 'thinking'; label: string }
  | { type: 'tool'; id: string; name: string; state: 'running' | 'done' | 'error'; label: string; kind: 'read' | 'search' | 'create' | 'update' | 'discover' | 'studio'; appId?: string; doorId?: string; record?: RecordLink; found?: RecordLink[]; place?: PlaceLink; detail?: string; outcome?: 'denied' | 'failed'; foldedInto?: string; parent?: { title: string; doorName?: string }; subject?: boolean }
  | { type: 'text'; text: string };

export interface AskContext {
  appId?: string;
  doorId?: string;
  period?: { start: string; endExclusive: string };
  timezone?: string;
  personId?: string;
  /** Ce que l'ecran montre : vue, recherche, filtres, archives, fiche ouverte (un identifiant, relu par le serveur). */
  view?: string;
  search?: string;
  filters?: string[];
  archives?: boolean;
  recordId?: string;
}

const FUNCTIONS_REGION = 'europe-west1';
const CALLABLE_ASK_TAKI = 'askTaki';
const bound = new WeakSet<Functions>();

export async function askTaki(
  config: TakiConfig,
  conversationId: string,
  requestId: string,
  messages: AskLine[],
  context: AskContext,
  onEvent: (event: AskEvent) => void,
  signal: AbortSignal,
  resumeId?: string,
  /** « Reponse de Lia prete » dans la boite interne (`notifierQuandPrete`, docs/NOTIFICATIONS.md). */
  notification?: { sujetId: string; titreSujet?: string },
): Promise<AskReply> {
  const auth = getAuthInstance();
  if (!auth?.currentUser) throw new Error('Reconnectez-vous pour parler à Lia.');
  const functions = getFunctions(auth.app, FUNCTIONS_REGION);
  const emulator = config.emulators?.functions;
  if (emulator && !bound.has(functions)) {
    connectFunctionsEmulator(functions, emulator.host, emulator.port);
    bound.add(functions);
  }
  const callable = httpsCallable<{ conversationId: string; requestId: string; messages: AskLine[]; context: AskContext; resumeId?: string; sujetId?: string; titreSujet?: string }, AskReply>(functions, CALLABLE_ASK_TAKI, {
    timeout: 250_000,
  });
  const run = async () => {
    const stream = await callable.stream({ conversationId, requestId, messages, context, ...(resumeId ? { resumeId } : {}), ...(notification ?? {}) }, { signal });
    const consume = (async () => {
      for await (const value of stream.stream) {
        if (value && typeof value === 'object' && 'type' in value) onEvent(value as AskEvent);
      }
    })();
    const [result] = await Promise.all([stream.data, consume]);
    if (!result || typeof result.text !== 'string' || !Array.isArray(result.toolsUsed)) {
      throw new Error('Réponse illisible du serveur.');
    }
    return result;
  };
  try {
    return await run();
  } catch (error) {
    if (!needsFreshToken(error) || signal.aborted) throw new AskTakiError(askTakiErrorMessage(error), false, refusedBeforeWork(error));
    // Jeton refuse par le serveur (droits changes, session relancee) : un nouveau jeton, UN nouvel essai.
    // Le serveur a refuse AVANT de commencer : rien n'a ete fait, rejouer est sans risque.
    try { await auth.currentUser?.getIdToken(true); }
    catch { throw new AskTakiError(askTakiErrorMessage(error), true); }
    try { return await run(); }
    catch (again) { throw new AskTakiError(askTakiErrorMessage(again), needsFreshToken(again), refusedBeforeWork(again)); }
  }
}
